import React, { useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import styled from 'styled-components/macro';

import { useTranslation } from 'react-i18next';
import { useScopes } from '../../../auth/accessToken/Allow';
import Card from '../../../components/Card';
import Icon from '../../../components/Icons';
import { LoadingOverlay } from '../../../components/Loading';
import Page, { PageButtonLink } from '../../../components/Page';
import { H1 } from '../../../components/Typography';
import { updateParams } from '../../../helpers/urlParams';
import { Discount } from '../../../types/discounts';
import { canWriteDiscounts } from '../../utils/accessUtils';
import { FetchDiscountsReason } from '../actions';
import { addStatus } from '../helpers';
import { State } from '../reducer';
import DiscountsTable from './DiscountsTable';
import Filters from './Filters';
import Paging from './Paging';

interface DiscountsListsProps {
    discountsListState: State;
    fetchDiscounts: (accountId: string, params: URLSearchParams, reason: FetchDiscountsReason) => void;
    fetchCampaigns: (accountId: string, params: URLSearchParams) => void;
}

const getNextPageArgs = (discounts: Discount[]): { [key: string]: string } => {
    if (discounts.length === 0) {
        return {};
    }
    const last = discounts[discounts.length - 1];
    return { starting_after: last.id || '' };
};

const DiscountsLists = (
    {
        discountsListState,
        fetchDiscounts,
        fetchCampaigns,
    }: DiscountsListsProps
) => {
    const { accountId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const scopes = useScopes();

    useEffect(() => {
        if (accountId) {
            fetchDiscounts(accountId, new URLSearchParams([['limit', '100']]), FetchDiscountsReason.mount);
            fetchCampaigns(accountId, new URLSearchParams([['limit', '100']]));
        }
    }, [accountId, fetchDiscounts, fetchCampaigns]);

    const discounts = discountsListState.discounts;

    if (!discounts || !accountId) {
        return <LoadingOverlay />;
    }

    const { isLoading, params, hasNext, campaigns } = discountsListState;
    const nextPageArgs = getNextPageArgs(discounts);
    const nextParams = updateParams(params, { ...nextPageArgs });
    const next = () => {
        fetchDiscounts(accountId, nextParams, FetchDiscountsReason.next);
    };

    const discountsWithStatus = addStatus(discounts);

    const discountType = location.hash.substr(1);

    let discountList = discountsWithStatus;
    if (
        discountType === 'active' ||
        discountType === 'scheduled' ||
        discountType === 'completed' ||
        discountType === 'deleted') {
        discountList = discountsWithStatus.filter(discount => discount.status === discountType);
    }

    return (
        <Page
            title={
                <>
                    <Title>{t('marketing.campaign.discounts_list.title')}</Title>
                    {canWriteDiscounts(scopes) &&
                        <PageButtonLink to={`/${accountId}/marketing/campaigns/new`}>
                            <Icon icon="document" />
                            {t('marketing.campaign.discounts_list.create_new')}
                        </PageButtonLink>
                    }
                </>
            }
        >
            <Card noPadding>
                <Filters
                    history={history}
                    location={location}
                    refresh={() => fetchDiscounts(accountId, params, FetchDiscountsReason.refresh)}
                />
                <Wrapper>
                    <DiscountsTable discounts={discountList} campaigns={campaigns} />
                    <RelativeGroup>
                        <Paging next={next} hasNext={hasNext} isLoading={isLoading} />
                        {isLoading && <LoadingOverlay />}
                    </RelativeGroup>
                </Wrapper>
            </Card>
        </Page>
    );
};

const Title = styled(H1)`
    display: inline-block;
`;

const Wrapper = styled.div`
    position: relative;
    box-sizing: border-box;
`;

const RelativeGroup = styled.div`
    position: relative;
    box-sizing: border-box;
`;

export default DiscountsLists;
